/**
 * ExpedienteUploader — one card per expediente. The operator types the
 * `expediente_id` (a CURP under the EXACER profile — the contract treats the
 * id as opaque; the profile's rule makes it a CURP), picks the 3 docs
 * (INE / certificado / foto), then uploads: `signUploads` declares each
 * document's `tipo` and mints one opaque `upload_url` per document, and each
 * File is PUT directly to its URL with a per-file progress bar.
 */

import { useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { api, isApiError } from "../../api/useApi";
import { IS_DEMO, type SignUploadResponse } from "../../api/client";
import { ProblemAlert } from "../../components/ProblemAlert";
import { demoSign } from "./demo";
import { isValidCurp } from "./folderScan";
import { putBytes } from "./putBytes";
import {
  contentTypeFor,
  DOC_KIND_LABEL,
  isRequiredDoc,
  newSlots,
  type DocKind,
  type DocSlot,
} from "./uploadTypes";

type Props = {
  roundId: string;
  /** Removes this card from the parent list. */
  onRemove: () => void;
  /** Bubbles up when all 3 docs for this expediente finished uploading. */
  onUploaded: (expedienteId: string) => void;
};

function statusChip(slot: DocSlot) {
  switch (slot.status) {
    case "done":
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleOutlineIcon />}
          label="Cargado"
        />
      );
    case "error":
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineIcon />}
          label="Error"
        />
      );
    case "signing":
      return <Chip size="small" color="info" variant="outlined" label="Firmando…" />;
    case "uploading":
      return (
        <Chip size="small" color="info" variant="outlined" label={`${slot.progress}%`} />
      );
    default:
      return slot.file ? (
        <Chip size="small" variant="outlined" label="Listo para cargar" />
      ) : (
        <Chip size="small" variant="outlined" label="Sin archivo" />
      );
  }
}

export function ExpedienteUploader({ roundId, onRemove, onUploaded }: Props) {
  const [expedienteId, setExpedienteId] = useState("");
  const [slots, setSlots] = useState<DocSlot[]>(newSlots);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fileInputs = useRef<Record<DocKind, HTMLInputElement | null>>({
    ine: null,
    ine_reverso: null,
    certificado: null,
    foto: null,
  });

  // The INE back is optional: a required slot must be picked/done; an unpicked
  // optional slot doesn't block submit or count against "all done".
  const allDone = slots.every(
    (s) => s.status === "done" || (s.file === null && !isRequiredDoc(s.kind)),
  );
  const allPicked = slots.every((s) => s.file !== null || !isRequiredDoc(s.kind));
  // El expediente_id debe ser una CURP (regla del perfil EXACER) — se valida en
  // el cliente para que el PROFILE_ID_VIOLATION del servidor nunca dispare.
  const idValid = isValidCurp(expedienteId.trim().toUpperCase());
  const canUpload = idValid && allPicked && !busy && !allDone;

  function pickFile(kind: DocKind, file: File | null) {
    setError(null);
    setSlots((prev) =>
      prev.map((s) =>
        s.kind === kind
          ? { ...s, file, status: "pending", progress: 0, error: undefined }
          : s,
      ),
    );
  }

  function setSlot(kind: DocKind, patch: Partial<DocSlot>) {
    setSlots((prev) => prev.map((s) => (s.kind === kind ? { ...s, ...patch } : s)));
  }

  async function handleUpload() {
    const id = expedienteId.trim().toUpperCase();
    if (!idValid || !allPicked) return;
    setBusy(true);
    setError(null);

    try {
      // 1) Sign: one FileSpec per picked doc, each declaring its tipo.
      setSlots((prev) =>
        prev.map((s) => ({ ...s, status: "signing", progress: 0, error: undefined })),
      );
      const files = slots
        .filter((s): s is DocSlot & { file: File } => s.file !== null)
        .map((s) => ({
          tipo: s.kind,
          content_type: contentTypeFor(s.file),
        }));

      let signed: SignUploadResponse;
      if (IS_DEMO) {
        signed = demoSign(roundId, id, files);
      } else {
        const res = await api.POST("/uploads/sign", {
          body: { round_id: roundId, expediente_id: id, files },
        });
        signed = res.data!;
      }

      // 2) PUT each File to its opaque upload_url. Match signed entries back to
      //    slots by their declared tipo.
      let anyFailed = false;
      await Promise.all(
        signed.uploads.map(async (u) => {
          const kind = u.tipo as DocKind;
          const slot = slots.find((s) => s.kind === kind);
          if (!slot?.file) return;
          setSlot(kind, { status: "uploading", progress: 0 });
          try {
            // Same Content-Type we signed with (must match for cloud signed URLs).
            await putBytes(u.upload_url, slot.file, contentTypeFor(slot.file), (pct) =>
              setSlot(kind, { progress: pct }),
            );
            setSlot(kind, { status: "done", progress: 100 });
          } catch (err) {
            anyFailed = true;
            setSlot(kind, {
              status: "error",
              error: err instanceof Error ? err.message : "Carga fallida.",
            });
          }
        }),
      );

      if (!anyFailed) onUploaded(id);
    } catch (err: unknown) {
      // signUploads failure (RFC-7807 by code via ProblemAlert; 401/403 by guard)
      setSlots((prev) =>
        prev.map((s) => (s.status === "signing" ? { ...s, status: "pending" } : s)),
      );
      if (isApiError(err)) setError(err);
      else setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <TextField
            label="CURP (ID del expediente)"
            placeholder="18 caracteres"
            value={expedienteId}
            onChange={(e) => setExpedienteId(e.target.value.toUpperCase())}
            size="small"
            disabled={busy || allDone}
            required
            error={!idValid && (allPicked || busy || expedienteId.length > 0)}
            helperText={
              !idValid && (allPicked || busy || expedienteId.length > 0)
                ? "Captura la CURP del expediente (regla del perfil EXACER)."
                : " "
            }
            sx={{ minWidth: 220 }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {allDone && (
              <Chip
                size="small"
                color="success"
                variant="filled"
                icon={<CheckCircleOutlineIcon />}
                label="Expediente cargado"
              />
            )}
            <Tooltip title="Quitar expediente">
              <span>
                <IconButton onClick={onRemove} disabled={busy} aria-label="Quitar expediente">
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack spacing={1.5}>
          {slots.map((slot) => (
            <Box key={slot.kind}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, alignItems: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, width: 92 }}>
                    {DOC_KIND_LABEL[slot.kind]}
                  </Typography>
                  <input
                    ref={(el) => {
                      fileInputs.current[slot.kind] = el;
                    }}
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    onChange={(e) => pickFile(slot.kind, e.target.files?.[0] ?? null)}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileOutlinedIcon />}
                    onClick={() => fileInputs.current[slot.kind]?.click()}
                    disabled={busy || slot.status === "done"}
                  >
                    {slot.file ? "Cambiar" : "Elegir archivo"}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 220,
                    }}
                    title={slot.file?.name}
                  >
                    {slot.file?.name ?? "Ningún archivo seleccionado"}
                  </Typography>
                </Stack>
                {statusChip(slot)}
              </Stack>
              {slot.status === "uploading" && (
                <LinearProgress
                  variant="determinate"
                  value={slot.progress}
                  sx={{ mt: 0.75, borderRadius: 1 }}
                />
              )}
              {slot.status === "error" && slot.error && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                  {slot.error}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>

        {error && <ProblemAlert error={error} />}

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={handleUpload}
            disabled={!canUpload}
          >
            {allDone
              ? "Cargado"
              : busy
                ? "Cargando…"
                : "Cargar documentos"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
